import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createContext, useContextSelector } from 'use-context-selector';

import { PERSPECTIVE_TOKEN_KEY } from '@/utils/Auth';

import { getAccountOwnerPerspectiveToken } from '@/services/AccountOwnerPerspectiveWallet/AccountOwnerPerspectiveWallet';
import { getUserDetailsById } from '@/services/InsertData/request';

import { IsMode, IViewMode, Mode } from './type';

import { localStorageSeguro } from '@/lib/storage';

const KEY = 'g_mode';

export const ViewModeContext = createContext<IViewMode>({} as IViewMode);

export const ViewModeProvider = ({ children }: { children: React.ReactNode }) => {
  const [mode, setMode] = useState<Mode>({} as Mode);
  const [perspectiveToken, setPerspectiveToken] = useState<string | null>(null);

  const { search } = window.location;
  const params = new URLSearchParams(search);

  const getUserInfo = async (id: Mode['user_id']) => {
    const response = await getUserDetailsById(id);
    setMode((prev) => ({
      ...prev,
      is: prev.is,
      user_info: response,
      user_id: prev.user_id,
    }));
  };

  const getPerspectiveToken = async (userId: number) => {
    try {
      const tokenResponse = await getAccountOwnerPerspectiveToken(userId);
      const token = tokenResponse.access_token; // assumindo que a resposta tem uma propriedade 'access'

      // Armazena o token de perspectiva
      localStorage.setItem(PERSPECTIVE_TOKEN_KEY, token);
      setPerspectiveToken(token);

      return token;
    } catch (error) {
      console.error('Erro ao obter token de perspectiva:', error);
      return null;
    }
  };

  const getMode = useCallback(async () => {
    const isModeExistInQueryParams = params.get('mode');
    const userIdExistInQueryParams = params.get('user_id');

    if (isModeExistInQueryParams && userIdExistInQueryParams) {
      const formatUserId = parseInt(userIdExistInQueryParams, 10);
      const response = getUserInfo(formatUserId);

      // Busca o token de perspectiva para o usuário
      await getPerspectiveToken(formatUserId);

      const newMode = {
        is: isModeExistInQueryParams as IsMode,
        user_id: formatUserId,
        user_info: response,
      };

      return newMode;
    }
    return null;
  }, []);

  const exitViewMode = useCallback(() => {
    localStorage.removeItem(KEY);
    localStorage.removeItem(PERSPECTIVE_TOKEN_KEY); // Remove o token de perspectiva
    setPerspectiveToken(null);

    params.delete('mode');
    params.delete('user_id');
    let userType = '/painel-perspectiva-do-proprietario';
    if (mode?.user_info?.roles?.includes('Host')) {
      userType = '/painel-perspectiva-do-anfitriao';
    } else if (mode?.user_info?.roles?.includes('Partner')) {
      userType = '/painel-perspectiva-do-parceiro';
    }
    window.history.replaceState({}, '', userType);
    window.location.reload();
  }, [mode]);

  // Função para atualizar o token de perspectiva quando um novo host é selecionado
  const updatePerspectiveToken = useCallback(async (userId: number) => {
    await getPerspectiveToken(userId);
  }, []);

  useEffect(() => {
    const handleGetMe = async () => {
      const validateMode: any = await getMode();
      setMode(validateMode);

      if (validateMode?.user_id) {
        getUserInfo(validateMode.user_id);
      }
    };

    handleGetMe();
  }, []);

  // Carrega token de perspectiva do localStorage na inicialização
  useEffect(() => {
    const storedToken = localStorageSeguro()?.getItem(PERSPECTIVE_TOKEN_KEY);
    if (storedToken) {
      setPerspectiveToken(storedToken);
    }
  }, []);

  const value = {
    mode,
    exitViewMode,
    perspectiveToken,
    updatePerspectiveToken,
  };

  return (
    <ViewModeContext.Provider value={value}>
      {/* <Message /> */}
      {children}
    </ViewModeContext.Provider>
  );
};

export const useViewMode = () => {
  const mode = useContextSelector(ViewModeContext, (context) => context.mode);
  const exitViewMode = useContextSelector(ViewModeContext, (context) => context.exitViewMode);
  const perspectiveToken = useContextSelector(
    ViewModeContext,
    (context) => context.perspectiveToken,
  );
  const updatePerspectiveToken = useContextSelector(
    ViewModeContext,
    (context) => context.updatePerspectiveToken,
  );

  const viewModeInfo = useContextSelector(ViewModeContext, (context) => context.mode?.user_info);

  const isViewMode = useMemo(() => mode?.is === 'view', [mode]);

  return {
    mode,
    isViewMode,
    exitViewMode,
    viewModeInfo,
    perspectiveToken,
    updatePerspectiveToken,
  };
};
